<template>
  <div class="dataview-container">
    <!-- Table -->
    <DataView
      ref="dataViewRef"
      v-bind="tableConfigRef"
      :class="{ 'from-user-has-groups': fromUserHasGroups }"
    >
      <template #table__numeroUsuarios__body="{ data }">
        {{ data.numeroUsuarios }}
      </template>
      <template #list__numeroUsuarios="{ data }">
        {{ data.numeroUsuarios }}
      </template>

      <!-- custom numeroInformes content -->
      <template #table__numeroInformes__body="{ data }">
        <div
          v-if="data.numeroInformes > 0"
          class="text-blue-200 cursor-pointer text-primary font-bold"
          @click="showUserGroupsHasReportsModal(data)"
        >
          {{ data.numeroInformes }}
        </div>
        <div v-else>0</div>
      </template>
      <template #list__numeroInformes="{ data }">
        <div
          v-if="data.numeroInformes > 0"
          class="text-blue-200 cursor-pointer text-primary font-bold"
          @click="showUserGroupsHasReportsModal(data)"
        >
          {{ data.numeroInformes }}
        </div>
        <div v-else>0</div>
      </template>
    </DataView>

    <div v-if="loading" class="progress-bar-container">
      <ProgressBar class="progress-bar" mode="indeterminate" />
    </div>
  </div>

  <!-- Modal -->
  <UsersGroupsDialogView ref="modalRef" @saved="updateTableList" />

  <!-- User has reports modal -->
  <UserHasReportsDialog
    ref="userHasReportsModalRef"
    :fromUserGroupHasReports="true"
  />

  <!-- User group has users modal -->
  <UserGroupHasUsersDialogView
    ref="userGroupHasUsersModalRef"
    @handleUpdateTableList="updateTableList()"
  />
</template>

<script setup>
import { useStore } from "vuex";
import { computed, ref } from "vue";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { DataView, useImmer } from "@sttedra_ui/framework";
import ProgressBar from "primevue/progressbar";

import useTableConfig from "@/pages/_root/users-groups/users-groups-table.view-config.js";
import UsersGroupsDialogView from "@/pages/_root/users-groups-dialog/UsersGroupsDialogView.vue";
import { appUserGroupDelete } from "@/api/users-groups/users-groups.api.js";
import UserGroupHasUsersDialogView from "@/pages/_root/user-group-has-users-dialog/UserGroupHasUsersDialogView.vue"; // eslint-disable-line
import UserHasReportsDialog from "@/pages/_root/user-has-reports-dialog/UserHasReportsDialog.vue";

// #region Props, Emits
const props = defineProps({
  fromUserHasGroups: {
    type: Number,
    default: null,
  },
});
// #endregion

// #region Params, Ref, Reactives, Computed
const store = useStore();

const loading = computed(() => store.getters.loadingUserGroups);

const confirm = useConfirm();
const toast = useToast();
const modalRef = ref(null);
const dataViewRef = ref(null);
const tableConfig = new useTableConfig(
  props.fromUserHasGroups,
  buildModal,
  buildUserGroupHasUsersModal,
  deleteRegister
);
const { ref: tableConfigRef } = useImmer(tableConfig);
const userGroupHasUsersModalRef = ref(null);
const userHasReportsModalRef = ref(null);
// #endregion

// #region Functions
// Open the user group has reports modal with the data
function showUserGroupsHasReportsModal(data) {
  userHasReportsModalRef.value.showModal(data);
}

function buildModal(event = null) {
  modalRef.value.showModal(event ? event.data : null);
}

// Open de user group has users modal
function buildUserGroupHasUsersModal(event) {
  const data = event.data;
  data.id = data.idGrupo;

  userGroupHasUsersModalRef.value.showModal(event.data);
}

function deleteRegister({ data }) {
  confirm.require({
    message: "¿Desea borrar el registro?",
    header: "Borrar",
    icon: "pi pi-exclamation-triangle",
    rejectClass: "p-button-secondary p-button-outlined",
    accept: async () => {
      const response = await appUserGroupDelete(data.idGrupo);

      if (response == true || response == "") {
        toast.add({
          severity: "success",
          summary: "Registro borrado",
          detail: `Se ha borrado el registro correctamente`,
          life: 3000,
        });

        updateTableList();
      } else {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Ha ocurrido un error al borrar el registro",
          life: 3000,
        });
      }
    },
  });
}

function updateTableList() {
  dataViewRef.value.debounceRefreshAndUpdate();
}
// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
defineExpose({
  updateTableList,
});
// #endregion
</script>

<style lang="scss" scoped>
.from-user-has-groups {
  :deep(.smart-content__center) {
    padding: 0;
  }
}

.dataview-container {
  position: relative;

  .progress-bar-container {
    position: absolute;
    inset: 0;
    background-color: rgba(white, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;

    .progress-bar {
      width: 80%;
      max-width: 15rem;
      height: 6px;
    }
  }
}
</style>
